import { useState } from 'react'
import { Smartphone } from 'lucide-react'
import { useI18n } from '../i18n.jsx'
import FactoryScene from './FactoryScene.jsx'
import useFactoryOrientation from './useFactoryOrientation.js'
import './factory-section.css'

const advantages = ['owned', 'efficiency', 'quality']
const motionLabels = {
  en: ['Enable motion view', 'Motion view enabled', 'Motion access denied'],
  de: ['Bewegungsansicht aktivieren', 'Bewegungsansicht aktiviert', 'Bewegungszugriff verweigert'],
  es: ['Activar vista de movimiento', 'Vista de movimiento activada', 'Acceso al movimiento denegado'],
  it: ['Attiva vista movimento', 'Vista movimento attiva', 'Accesso al movimento negato'],
  fr: ['Activer la vue par mouvement', 'Vue par mouvement activée', 'Accès au mouvement refusé'],
  'zh-CN': ['启用体感视角', '体感视角已启用', '体感权限未获允许'],
  'zh-TW': ['啟用體感視角', '體感視角已啟用', '體感權限未獲允許'],
}

function FactoryArtwork({ kind, title }) {
  return <svg className="factory-vector-fallback" viewBox="0 0 320 220" role="img" aria-label={title}>
    <g stroke="#1674cf" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
      {kind === 'owned' && <>
        <path fill="#e3eaf0" stroke="none" d="m24 154 125-70 148 82-125 49Z" />
        <path fill="#fff" d="m35 143 107-61 140 70v15l-107 46-140-55Z" />
        <path fill="#d8e5ee" d="m35 143 140 62v-15l-140-62Zm140 47 107-38v15l-107 46Z" />
        <path fill="#c7e6f5" d="M49 84v57l126 63v-57Z" />
        <path fill="#fff" d="m175 147 94-53v57l-94 53Z" />
        <path fill="#fff" d="m49 84 94-53 126 63-94 53Z" />
        <path fill="#e5f2fa" d="m94 78 52-29 78 39-52 29Z" />
        <path fill="#0082e5" d="m106 78 40-22 64 32-39 23Z" />
        <path fill="none" d="m49 103 126 63 94-53M49 122l126 63 94-53m-103 10v57M67 93v57m18-48v57m18-48v57m18-48v57m18-48v57m18-48v57m37-53v57m19-68v57m19-68v57m18-67v57" />
        <path fill="#fff" d="m79 143 48 24v38l-48-24Z" />
        <path fill="#69bcef" d="m90 157 25 12v23l-25-12Z" />
        <path fill="#fff" d="m69 144 15-8 52 26-15 8Z" />
        <path fill="none" d="M73 146v23m59-5v23" />
      </>}
      {kind === 'efficiency' && <>
        <path fill="#e3eaf0" stroke="none" d="m22 145 151-84 128 73-151 84Z" />
        <path fill="#fff" d="m34 135 145-80 105 59-145 81Z" />
        <path fill="#c7e6f5" d="m34 135 105 60v12L34 147Zm105 60 145-81v12l-145 81Z" />
        <path fill="#fff" d="m44 122 137-76 89 50-137 76Z" />
        <path fill="#69bcef" d="m44 122 89 50v10l-89-50Z" />
        <path fill="#c7e6f5" d="m133 172 137-76v10l-137 76Z" />
        <path fill="#e5f2fa" d="m58 119 123-68 75 42-123 68Z" />
        <path fill="none" d="m76 110 75 42m-58-52 75 42m-58-52 75 42m-58-52 75 42m-58-52 75 42m-58-52 75 42" />
        <path fill="#fff" d="m72 88 28-16 31 18-28 16Z" />
        <path fill="#c7e6f5" d="m72 88 31 18v24l-31-18Z" />
        <path fill="#69bcef" d="m103 106 28-16v24l-28 16Z" />
        <path fill="#fff" d="m139 51 28-16 31 18-28 16Z" />
        <path fill="#c7e6f5" d="m139 51 31 18v24l-31-18Z" />
        <path fill="#69bcef" d="m170 69 28-16v24l-28 16Z" />
        <path fill="#fff" d="m213 76 27-15 30 17-27 15Z" />
        <path fill="#c7e6f5" d="m213 76 30 17v24l-30-17Z" />
        <path fill="#0082e5" d="m243 93 27-15v24l-27 15Z" />
        <path fill="none" strokeWidth="2" d="m118 79 14-8m-7-1 7 1-2 6m-25 75 14-8m-7-1 7 1-2 6" />
        <path fill="#fff" d="m53 138 9 5v28l-9-5Zm71 40 9 5v28l-9-5Zm124-67 9-5v28l-9 5Z" />
      </>}
      {kind === 'quality' && <>
        <path fill="#e3eaf0" stroke="none" d="m30 149 142-78 121 68-142 78Z" />
        <path fill="#fff" d="m45 132 119-66 111 62-119 66Z" />
        <path fill="#c7e6f5" d="m45 132 111 62v11L45 143Zm111 62 119-66v11l-119 66Z" />
        <path fill="#fff" d="m59 145 9 5v38l-9-5Zm87 48 9 5v20l-9-5Zm105-49 9-5v38l-9 5Z" />
        <path fill="#c7e6f5" d="m183 127 24 14 18-10-24-14Z" />
        <path fill="#fff" d="m201 84 7 4v42l-7-4Z" />
        <path fill="#c7e6f5" d="m163 35 70 39v59l-70-39Z" />
        <path fill="#fff" d="m163 35 7-4 70 39-7 4Zm70 39 7-4v59l-7 4Z" />
        <path fill="#e5f2fa" d="m172 50 52 29v39l-52-29Z" />
        <path fill="none" stroke="#0082e5" strokeWidth="3" d="m184 75 8 13 18-9" />
        <path fill="#fff" d="m79 121 32-18 38 21-32 18Z" />
        <path fill="#c7e6f5" d="m79 121 38 21v17l-38-21Z" />
        <path fill="#69bcef" d="m117 142 32-18v17l-32 18Z" />
        <path fill="#0082e5" d="m93 121 18-10 23 13-18 10Z" />
        <path fill="#fff" d="m156 151 25-14 31 17-25 14Z" />
        <path fill="none" d="m165 151 5 3 7-4m-1 9 5 3 7-4m-2-10 10 6" />
      </>}
    </g>
  </svg>
}

export default function FactorySection() {
  const { t, language } = useI18n()
  const { orientation, canRequest, permission, requestPermission } = useFactoryOrientation()
  const labels = motionLabels[language] ?? motionLabels.en
  const motionLabel = labels[permission === 'granted' ? 1 : permission === 'denied' ? 2 : 0]
  const [selected, setSelected] = useState(null)
  const toggle = id => setSelected(current => current === id ? null : id)
  return <section id="factory" className="factory-section uniform-section" data-testid="factory-section">
    <div className="section-inner factory-inner">
      <div className="factory-heading">
        <div className="factory-heading-row">
          <h2>{t('site.factory_title')}</h2>
          {canRequest && <button type="button" className="factory-motion-button" onClick={requestPermission} title={motionLabel} aria-label={motionLabel} aria-pressed={permission === 'granted'} disabled={permission === 'pending' || permission === 'granted' || permission === 'denied'}><Smartphone size={20} aria-hidden="true" /><span className="factory-motion-tooltip" role="tooltip">{motionLabel}</span></button>}
        </div>
        <p className="factory-introduction">{t('site.factory_description')}</p>
      </div>
      <div className="factory-advantages">
        {advantages.map(id => <article className="factory-advantage" key={id} data-factory-advantage={id} data-selected={selected === id} role="button" tabIndex={0} aria-pressed={selected === id} aria-label={t(`site.factory_${id}_title`)} aria-describedby={`factory-description-${id}`}
          onPointerEnter={event => { if (event.pointerType === 'mouse') setSelected(id) }}
          onPointerLeave={event => { if (event.pointerType === 'mouse' && !event.currentTarget.matches(':focus-visible')) setSelected(current => current === id ? null : current) }}
          onFocus={event => { if (event.currentTarget.matches(':focus-visible')) setSelected(id) }}
          onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setSelected(current => current === id ? null : current) }}
          onClick={() => { if (window.matchMedia('(hover: none)').matches) toggle(id) }}
          onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle(id) } }}>
          <FactoryScene kind={id} selected={selected === id} orientation={orientation} label={t(`site.factory_${id}_title`)} fallback={<FactoryArtwork kind={id} title={t(`site.factory_${id}_title`)} />} />
          <div className="factory-advantage-copy"><h3>{t(`site.factory_${id}_title`)}</h3><p id={`factory-description-${id}`}>{t(`site.factory_${id}_description`)}</p></div>
        </article>)}
      </div>
    </div>
  </section>
}
